import React, { useCallback, useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { HERO_DEMO_SCENARIOS } from '../data/heroDemoScenarios';
import './HeroCitationConsole.css';

const formatAnswerLine = (line) => {
  const parts = line.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const HeroCitationConsole = () => {
  const [scenarioIndex, setScenarioIndex] = useState(0);
  const [expandedRowIndex, setExpandedRowIndex] = useState(null);
  const [focusedRowIndex, setFocusedRowIndex] = useState(0);

  const barFillRefs = useRef([]);
  const barTimelineRef = useRef(null);
  const skipNextBarAnimationRef = useRef(false);
  const chipRefs = useRef([]);
  const rowRefs = useRef([]);
  const liveRef = useRef(null);

  const scenario = HERO_DEMO_SCENARIOS[scenarioIndex];

  const announce = useCallback((message) => {
    if (liveRef.current) {
      liveRef.current.textContent = message;
    }
  }, []);

  const setBarsFinal = useCallback((rows) => {
    rows.forEach((row, i) => {
      const el = barFillRefs.current[i];
      if (el) {
        gsap.set(el, { scaleX: row.score / 100 });
      }
    });
  }, []);

  const playBarFill = useCallback((rows) => {
    if (barTimelineRef.current) {
      barTimelineRef.current.kill();
      barTimelineRef.current = null;
    }

    barFillRefs.current = barFillRefs.current.slice(0, rows.length);

    if (prefersReducedMotion()) {
      setBarsFinal(rows);
      return;
    }

    rows.forEach((_, i) => {
      const el = barFillRefs.current[i];
      if (el) gsap.set(el, { scaleX: 0 });
    });

    const tl = gsap.timeline();
    rows.forEach((row, i) => {
      const el = barFillRefs.current[i];
      if (!el) return;
      tl.fromTo(
        el,
        { scaleX: 0 },
        { scaleX: row.score / 100, duration: 0.3, ease: 'power3.out' },
        i * 0.06
      );
    });
    barTimelineRef.current = tl;
  }, [setBarsFinal]);

  useEffect(() => {
    if (skipNextBarAnimationRef.current) {
      skipNextBarAnimationRef.current = false;
      setBarsFinal(scenario.retrieval);
    } else {
      playBarFill(scenario.retrieval);
    }
    return () => {
      if (barTimelineRef.current) {
        barTimelineRef.current.kill();
      }
    };
  }, [scenarioIndex, scenario.retrieval, playBarFill, setBarsFinal]);

  const selectScenario = useCallback((index, { animateBars = true } = {}) => {
    if (!animateBars) {
      skipNextBarAnimationRef.current = true;
    }
    setScenarioIndex(index);
    setExpandedRowIndex(null);
    setFocusedRowIndex(0);
    announce(`Showing example: ${HERO_DEMO_SCENARIOS[index].chipLabel}`);
  }, [announce]);

  const toggleRowExpand = useCallback((rowIndex) => {
    setExpandedRowIndex((prev) => (prev === rowIndex ? null : rowIndex));
  }, []);

  const handleChipKeyDown = (e, index) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const next = (index + 1) % HERO_DEMO_SCENARIOS.length;
      selectScenario(next, { animateBars: false });
      chipRefs.current[next]?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prev = (index - 1 + HERO_DEMO_SCENARIOS.length) % HERO_DEMO_SCENARIOS.length;
      selectScenario(prev, { animateBars: false });
      chipRefs.current[prev]?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      selectScenario(index, { animateBars: false });
    }
  };

  const handleRowKeyDown = (e, rowIndex) => {
    const rowCount = scenario.retrieval.length;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = Math.min(rowIndex + 1, rowCount - 1);
      setFocusedRowIndex(next);
      rowRefs.current[next]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = Math.max(rowIndex - 1, 0);
      setFocusedRowIndex(prev);
      rowRefs.current[prev]?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleRowExpand(rowIndex);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setExpandedRowIndex(null);
    }
  };

  return (
    <div className="hcc-root">
      <div
        id="hcc-panel"
        className="hcc-panel"
        role="tabpanel"
        aria-labelledby={`hcc-tab-${scenario.id}`}
      >
        <div className="hcc-chrome">
          <span>edge · briefing</span>
          <span className="hcc-chrome-release">{scenario.release}</span>
        </div>

        <div className="hcc-body">
          <div>
            <p className="hcc-label">Query</p>
            <p className="hcc-query-text">{scenario.question}</p>
          </div>

          <div>
            <p className="hcc-meta">
              RETRIEVED · {scenario.retrieval.length}{' '}
              {scenario.retrieval.length === 1 ? 'source' : 'sources'}
            </p>
            <ul className="hcc-retrieval-list" role="list">
              {scenario.retrieval.map((row, rowIndex) => {
                const isExpanded = expandedRowIndex === rowIndex;
                const previewId = `hcc-preview-${scenario.id}-${rowIndex}`;

                return (
                  <li key={row.file} role="listitem">
                    <button
                      type="button"
                      ref={(el) => { rowRefs.current[rowIndex] = el; }}
                      className={`hcc-retrieval-row${isExpanded ? ' is-expanded' : ''}`}
                      tabIndex={focusedRowIndex === rowIndex ? 0 : -1}
                      aria-expanded={isExpanded}
                      aria-controls={previewId}
                      onClick={() => toggleRowExpand(rowIndex)}
                      onKeyDown={(e) => handleRowKeyDown(e, rowIndex)}
                      onFocus={() => setFocusedRowIndex(rowIndex)}
                    >
                      <div className="hcc-row-summary">
                        <span className="hcc-filename">{row.file}</span>
                        <div className="hcc-bar-track" aria-hidden="true">
                          <div
                            ref={(el) => { barFillRefs.current[rowIndex] = el; }}
                            className="hcc-bar-fill"
                          />
                        </div>
                        <span className="hcc-score">{row.score}%</span>
                        <span
                          className={`hcc-status ${
                            row.verified ? 'hcc-status-verified' : 'hcc-status-good'
                          }`}
                        >
                          {row.verified ? '✓ Verified' : 'Good match'}
                        </span>
                      </div>
                      {isExpanded && (
                        <div id={previewId} className="hcc-row-preview">
                          <span className="hcc-preview-release">{scenario.release}</span>
                          {row.preview}
                        </div>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="hcc-answer">
            {scenario.answer.map((line, i) => (
              <p key={`${scenario.id}-answer-${i}`}>{formatAnswerLine(line)}</p>
            ))}
          </div>

          <div className="hcc-sources">
            <span className="hcc-sources-label">SOURCES</span>
            {scenario.sourceRef}
          </div>
        </div>
      </div>

      <ul className="hcc-chips" role="tablist" aria-label="Example Oracle HCM queries">
        {HERO_DEMO_SCENARIOS.map((item, index) => (
          <li key={item.id} role="presentation">
            <button
              type="button"
              id={`hcc-tab-${item.id}`}
              ref={(el) => { chipRefs.current[index] = el; }}
              className="hcc-chip"
              role="tab"
              aria-selected={scenarioIndex === index}
              aria-controls="hcc-panel"
              tabIndex={scenarioIndex === index ? 0 : -1}
              onClick={() => selectScenario(index)}
              onKeyDown={(e) => handleChipKeyDown(e, index)}
            >
              {item.chipLabel}
            </button>
          </li>
        ))}
      </ul>

      <div ref={liveRef} className="hcc-sr-only" aria-live="polite" aria-atomic="true" />
    </div>
  );
};

export default HeroCitationConsole;
